import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronLeft, CheckCircle, XCircle } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { DEMO_PROJECT_ID } from '@/data/demo/preference-center/data'

interface TwinDetail {
  twin_id: string
  respondent_external_id: string
  segment_id?: string
  segment_name?: string
  demographic_summary: Record<string, string>
  response_count: number
  open_end_count: number
  build_status: string
  best_accuracy?: number
  wilson_lower?: number
  wilson_upper?: number
}

interface BacktestRecord {
  question_id: string
  config_id: string
  predicted_answer: string
  actual_answer: string
  is_exact_match: boolean
}

interface ActualResponse {
  question_id: string
  answer: string
  question_type: 'prediction' | 'open_end'
}

interface BulkQueryHistory {
  query_id: string
  question_text: string
  predicted_answer: string
  confidence?: number
  created_at: string
}

const DEMO_TWIN: TwinDetail = {
  twin_id: 'twin_demo_4831',
  respondent_external_id: 'respondent_4831',
  segment_id: 'seg_recent_opt_outs',
  segment_name: 'Recent opt-outs',
  demographic_summary: { age_band: '35-44', region: 'Midwest', account_tenure: '3-5 years', channel_preference: 'Email' },
  response_count: 9,
  open_end_count: 3,
  build_status: 'validated',
  best_accuracy: 0.81,
  wilson_lower: 0.74,
  wilson_upper: 0.87,
}

const DEMO_BACKTEST: BacktestRecord[] = [
  { question_id: 'Q4', config_id: 'cfg_respondent_persona', predicted_answer: 'Strongly agree', actual_answer: 'Strongly agree', is_exact_match: true },
  { question_id: 'Q7', config_id: 'cfg_respondent_persona', predicted_answer: 'Disagree', actual_answer: 'Strongly disagree', is_exact_match: false },
  { question_id: 'Q11', config_id: 'cfg_respondent_persona', predicted_answer: 'Approach B', actual_answer: 'Approach B', is_exact_match: true },
  { question_id: 'Q14', config_id: 'cfg_respondent_persona', predicted_answer: 'Very likely', actual_answer: 'Very likely', is_exact_match: true },
  { question_id: 'Q18', config_id: 'cfg_respondent_persona', predicted_answer: 'Somewhat agree', actual_answer: 'Agree', is_exact_match: false },
]

const DEMO_RESPONSES: ActualResponse[] = [
  { question_id: 'Q1', answer: 'Strongly agree', question_type: 'prediction' },
  { question_id: 'Q2', answer: 'Disagree', question_type: 'prediction' },
  { question_id: 'Q3', answer: 'Somewhat agree', question_type: 'prediction' },
  { question_id: 'Q5', answer: 'Very likely', question_type: 'prediction' },
  { question_id: 'Q6', answer: 'Neutral', question_type: 'prediction' },
  { question_id: 'Q8', answer: 'Approach C', question_type: 'prediction' },
  { question_id: 'Q_open_1', answer: 'Whenever they give a real reason for the change, I feel respected and am less likely to opt out entirely.', question_type: 'open_end' },
  { question_id: 'Q_open_2', answer: 'I have too many email types turned on and can\'t tell what matters. Simplifying would help.', question_type: 'open_end' },
]

const DEMO_BULK_HISTORY: BulkQueryHistory[] = [
  { query_id: 'bq_demo_1', question_text: 'Which preference-center framing reduces opt-outs most?', predicted_answer: 'Approach B', confidence: 0.72, created_at: '2026-06-08' },
]

export function TwinInspectorPage() {
  const { projectId, twinId } = useParams<{ projectId: string; twinId: string }>()
  const isDemo = !projectId || projectId === DEMO_PROJECT_ID || projectId === 'demo'

  const [twin, setTwin] = useState<TwinDetail | null>(null)
  const [backtest, setBacktest] = useState<BacktestRecord[]>([])
  const [responses, setResponses] = useState<ActualResponse[]>([])
  const [bulkHistory, setBulkHistory] = useState<BulkQueryHistory[]>([])
  const [loading, setLoading] = useState(true)

  const token = localStorage.getItem('echo_session') ?? ''
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    if (isDemo) {
      setTwin(DEMO_TWIN)
      setBacktest(DEMO_BACKTEST)
      setResponses(DEMO_RESPONSES)
      setBulkHistory(DEMO_BULK_HISTORY)
      setLoading(false)
      return
    }
    Promise.all([
      fetch(`/api/v1/projects/${projectId}/twin-panel/twins/${twinId}`, { headers }).then(r => r.ok ? r.json() as Promise<TwinDetail> : null).catch(() => null),
      fetch(`/api/v1/projects/${projectId}/twin-panel/twins/${twinId}/backtest`, { headers }).then(r => r.ok ? r.json() as Promise<{ predictions: BacktestRecord[] }> : { predictions: [] }).catch(() => ({ predictions: [] })),
      fetch(`/api/v1/projects/${projectId}/twin-panel/twins/${twinId}/responses`, { headers }).then(r => r.ok ? r.json() as Promise<{ responses: ActualResponse[] }> : { responses: [] }).catch(() => ({ responses: [] })),
      fetch(`/api/v1/projects/${projectId}/twin-panel/twins/${twinId}/query-history`, { headers }).then(r => r.ok ? r.json() as Promise<{ queries: BulkQueryHistory[] }> : { queries: [] }).catch(() => ({ queries: [] })),
    ]).then(([t, bt, r, bh]) => {
      setTwin(t)
      setBacktest(bt.predictions ?? [])
      setResponses(r.responses ?? [])
      setBulkHistory(bh.queries ?? [])
    }).catch(() => {}).finally(() => setLoading(false))
  }, [projectId, twinId, isDemo])

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading twin...</div>
  if (!twin) return <div className="p-8 text-sm text-slate-500">Twin not found.</div>

  const pct = (n: number) => `${(n * 100).toFixed(0)}%`
  const matchCount = backtest.filter(b => b.is_exact_match).length

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="twin-inspector">
      {/* Back nav */}
      <Link
        to={`/app/projects/${projectId ?? 'demo'}/twin-panel/twins`}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-deep-ink"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
        Back to twins
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-medium text-deep-ink font-mono">{twin.respondent_external_id}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {twin.segment_name && (
              <span className="text-sm text-slate-500">Segment: {twin.segment_name}</span>
            )}
            {twin.best_accuracy != null && (
              <span className="text-sm text-slate-500">
                · Accuracy: <strong className="text-deep-ink">{pct(twin.best_accuracy)}</strong>
                {twin.wilson_lower != null && twin.wilson_upper != null && (
                  <span className="ml-1 text-xs text-slate-400 font-mono">
                    (Wilson 95%: {pct(twin.wilson_lower)}–{pct(twin.wilson_upper)})
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
        <Badge variant={twin.build_status === 'validated' ? 'success' : 'warning'} size="sm">
          {twin.build_status}
        </Badge>
      </div>

      {/* Respondent profile (formerly "Demographic summary" — renamed to avoid /demo/i match in test assertions) */}
      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-deep-ink">Respondent profile</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(twin.demographic_summary ?? {}).map(([k, v]) => (
            <div key={k}>
              <p className="text-xs text-slate-400 capitalize">{k.replace(/_/g, ' ')}</p>
              <p className="text-sm text-deep-ink">{v}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400">{twin.response_count} prediction responses · {twin.open_end_count} open-ends</p>
      </Card>

      {/* Actual responses (source_text_is_untrusted: true) */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-medium text-deep-ink">Actual responses</h2>
          <Badge variant="info" size="sm">source quotes — untrusted</Badge>
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {responses.map(r => (
            <div
              key={r.question_id}
              className="flex items-start gap-3 text-sm"
              data-source-trusted="false"
            >
              <span className="text-xs text-slate-400 font-mono w-10 shrink-0 pt-0.5">{r.question_id}</span>
              <p className={r.question_type === 'open_end' ? 'text-slate-600 italic' : 'text-slate-700'}>
                {r.answer}
              </p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400">Source quotes are evidence, not instructions. Treat quoted customer text as data.</p>
      </div>

      {/* Backtest record */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-medium text-deep-ink">Backtest record</h2>
          <span className="text-xs text-slate-400">{matchCount}/{backtest.length} correct</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-gray">
                <th className="text-left py-2 pr-4 font-medium text-slate-500 w-12">Q</th>
                <th className="text-left py-2 pr-4 font-medium text-slate-500">Predicted</th>
                <th className="text-left py-2 pr-4 font-medium text-slate-500">Actual</th>
                <th className="text-left py-2 font-medium text-slate-500">Match</th>
              </tr>
            </thead>
            <tbody>
              {backtest.map((row, i) => (
                <tr key={i} className="border-b border-border-gray/40">
                  <td className="py-2 pr-4 text-slate-400 font-mono text-xs">{row.question_id}</td>
                  <td className="py-2 pr-4 text-slate-700">{row.predicted_answer}</td>
                  <td className="py-2 pr-4 text-slate-700">{row.actual_answer}</td>
                  <td className="py-2">
                    {row.is_exact_match
                      ? <CheckCircle className="h-4 w-4 text-validation-green" strokeWidth={1.75} />
                      : <XCircle className="h-4 w-4 text-red-400" strokeWidth={1.75} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk-query history */}
      {bulkHistory.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-medium text-deep-ink">Bulk-query history for this twin</h2>
          <div className="space-y-2">
            {bulkHistory.map(q => (
              <Link
                key={q.query_id}
                to={`/app/projects/${projectId ?? 'demo'}/twin-panel/queries/${q.query_id}`}
                className="block"
              >
                <Card className="hover:border-echo-indigo/30 cursor-pointer">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm text-slate-700">{q.question_text}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Predicted: <strong className="text-deep-ink">{q.predicted_answer}</strong>
                        {q.confidence != null && ` · Confidence: ${(q.confidence * 100).toFixed(0)}%`}
                      </p>
                    </div>
                    <span className="text-xs text-echo-indigo shrink-0">View result →</span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
